import { useEffect, useMemo, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  listVendorAccounts,
  createVendorAccount,
  deleteVendorAccount,
  listSources,
  createSource,
  uploadImport,
  listVendorItems,
  createVendorItem,
  updateVendorItem,
  deleteVendorItem,
} from '../utils/vendorApi';

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-white/5 bg-white/5 p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-white">{title}</h2>
      {children}
    </section>
  );
}

function AccountCard({ account, onUpload, onDelete, uploading }) {
  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-base font-semibold text-white">{account.name}</div>
          {account.website && (
            <div className="text-sm text-amber-200">{account.website}</div>
          )}
          <div className="text-xs text-zinc-400">
            Updated {account.updated_at ? new Date(account.updated_at).toLocaleString() : '—'}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={uploading}
            onClick={() => onUpload(account)}
            className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-black transition hover:bg-amber-400 disabled:opacity-60"
          >
            {uploading ? 'Uploading…' : 'Upload CSV'}
          </button>
          <button
            type="button"
            onClick={() => onDelete(account)}
            className="rounded-lg border border-red-500/50 px-3 py-2 text-sm font-semibold text-red-200 transition hover:bg-red-500/10"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Vendors() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);

  const [itemForm, setItemForm] = useState({
    sku: '',
    title: '',
    unit_price: '',
    qty_min: 1,
    qty_step: 1,
    mpn: '',
    upc: '',
  });
  const nameRef = useRef(null);
  const websiteRef = useRef(null);

  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [accounts]
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listVendorAccounts();
      setAccounts(data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to load vendor accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = nameRef.current?.value?.trim();
    const website = websiteRef.current?.value?.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await createVendorAccount({ name, website });
      nameRef.current.value = '';
      if (websiteRef.current) websiteRef.current.value = '';
      await refresh();
      toast.success('Vendor account created');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  const ensureCsvSource = async (accountId) => {
    const sources = await listSources(accountId);
    const existing = sources.find((s) => s.type === 'csv');
    if (existing) return existing;
    return createSource(accountId, { type: 'csv' });
  };

  const handleUpload = async (account) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = async () => {
      if (!input.files?.length) return;
      const file = input.files[0];
      setUploadingId(account.id);
      try {
        const source = await ensureCsvSource(account.id);
        await uploadImport(source.id, file);
        toast.success('Import started');
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.error || 'Import failed');
      } finally {
        setUploadingId(null);
      }
    };
    input.click();
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Delete vendor "${account.name}"?`)) return;
    try {
      await deleteVendorAccount(account.id);
      toast.success('Vendor deleted');
      refresh();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Delete failed');
    }
  };

  const loadItems = async (accountId) => {
    if (!accountId) return;
    setItemsLoading(true);
    try {
      const data = await listVendorItems(accountId);
      setItems(data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to load items');
    } finally {
      setItemsLoading(false);
    }
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setEditingItemId(null);
    setItemForm({
      sku: '',
      title: '',
      unit_price: '',
      qty_min: 1,
      qty_step: 1,
      mpn: '',
      upc: '',
    });
    loadItems(account.id);
  };

  const handleSaveItem = async () => {
    if (!selectedAccount) return;
    try {
      if (editingItemId) {
        await updateVendorItem(editingItemId, itemForm);
        toast.success('Item updated');
      } else {
        await createVendorItem(selectedAccount.id, itemForm);
        toast.success('Item added');
      }
      setEditingItemId(null);
      setItemForm({
        sku: '',
        title: '',
        unit_price: '',
        qty_min: 1,
        qty_step: 1,
        mpn: '',
        upc: '',
      });
      loadItems(selectedAccount.id);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Save failed');
    }
  };

  const handleEditRow = (row) => {
    setEditingItemId(row.id);
    setItemForm({
      sku: row.sku || '',
      title: row.title || '',
      unit_price: row.price ?? '',
      qty_min: row.qty_min ?? 1,
      qty_step: row.qty_step ?? 1,
      mpn: row.mpn || '',
      upc: row.upc || '',
    });
  };

  const handleDeleteRow = async (row) => {
    if (!window.confirm(`Delete item ${row.sku}?`)) return;
    try {
      await deleteVendorItem(row.id);
      toast.success('Item deleted');
      loadItems(selectedAccount.id);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <Section title="Create Vendor">
        <form className="grid gap-4 md:grid-cols-2" onSubmit={handleCreate}>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-300">Name</span>
            <input
              ref={nameRef}
              type="text"
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-amber-400/60 focus:outline-none"
              placeholder="Acme Supply"
              required
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-zinc-300">Website (optional)</span>
            <input
              ref={websiteRef}
              type="text"
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-amber-400/60 focus:outline-none"
              placeholder="https://example.com"
            />
          </label>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-black transition hover:bg-amber-400 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Create Vendor'}
            </button>
          </div>
        </form>
      </Section>

      <Section title="Vendor Accounts">
        {loading ? (
          <p className="text-sm text-zinc-400">Loading vendors…</p>
        ) : sortedAccounts.length ? (
          <div className="space-y-3">
            {sortedAccounts.map((account) => (
              <div
                key={account.id}
                className={`cursor-pointer rounded-xl border ${selectedAccount?.id === account.id ? 'border-amber-400/70 bg-amber-500/5' : 'border-white/5 bg-zinc-900/60'} p-4 transition hover:border-amber-400/50`}
                onClick={() => handleSelectAccount(account)}
              >
                <AccountCard
                  account={account}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  uploading={uploadingId === account.id}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-zinc-400">No vendors yet. Create one above.</p>
        )}
      </Section>

      {selectedAccount && (
        <Section title={`Items · ${selectedAccount.name}`}>
          <div className="mb-4 grid gap-3 md:grid-cols-3">
            <input
              value={itemForm.sku}
              onChange={(e) => setItemForm((s) => ({ ...s, sku: e.target.value }))}
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              placeholder="SKU"
            />
            <input
              value={itemForm.title}
              onChange={(e) => setItemForm((s) => ({ ...s, title: e.target.value }))}
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              placeholder="Title"
            />
            <input
              value={itemForm.unit_price}
              onChange={(e) => setItemForm((s) => ({ ...s, unit_price: e.target.value }))}
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              placeholder="Price (USD)"
              type="number"
              min="0"
              step="0.01"
            />
            <input
              value={itemForm.mpn}
              onChange={(e) => setItemForm((s) => ({ ...s, mpn: e.target.value }))}
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              placeholder="MPN (optional)"
            />
            <input
              value={itemForm.upc}
              onChange={(e) => setItemForm((s) => ({ ...s, upc: e.target.value }))}
              className="rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              placeholder="UPC (optional)"
            />
            <div className="flex gap-3">
              <input
                value={itemForm.qty_min}
                onChange={(e) => setItemForm((s) => ({ ...s, qty_min: e.target.value }))}
                className="w-24 rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
                placeholder="Qty min"
                type="number"
                min="1"
              />
              <input
                value={itemForm.qty_step}
                onChange={(e) => setItemForm((s) => ({ ...s, qty_step: e.target.value }))}
                className="w-24 rounded-lg border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
                placeholder="Qty step"
                type="number"
                min="1"
              />
            </div>
          </div>
          <div className="mb-4 flex gap-3">
            <button
              type="button"
              onClick={handleSaveItem}
              className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-black transition hover:bg-amber-400"
            >
              {editingItemId ? 'Update Item' : 'Add Item'}
            </button>
            {editingItemId && (
              <button
                type="button"
                onClick={() => {
                  setEditingItemId(null);
                  setItemForm({
                    sku: '',
                    title: '',
                    unit_price: '',
                    qty_min: 1,
                    qty_step: 1,
                    mpn: '',
                    upc: '',
                  });
                }}
                className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/10"
              >
                Cancel
              </button>
            )}
          </div>

          {itemsLoading ? (
            <p className="text-sm text-zinc-400">Loading items…</p>
          ) : items.length ? (
            <div className="overflow-x-auto rounded-xl border border-white/5">
              <table className="min-w-full divide-y divide-white/5 text-sm">
                <thead className="bg-zinc-900/60 text-zinc-300">
                  <tr>
                    <th className="px-3 py-2 text-left">SKU</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Price</th>
                    <th className="px-3 py-2 text-left">Qty</th>
                    <th className="px-3 py-2 text-left">MPN</th>
                    <th className="px-3 py-2 text-left">UPC</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {items.map((row) => (
                    <tr key={row.id} className="bg-black/20">
                      <td className="px-3 py-2 font-mono text-xs text-amber-100">{row.sku}</td>
                      <td className="px-3 py-2 text-white">{row.title}</td>
                      <td className="px-3 py-2 text-amber-200">
                        {row.price != null ? `$${Number(row.price).toFixed(2)}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-zinc-300">
                        {row.qty_min || 1}
                        {row.qty_step ? ` / ${row.qty_step}` : ''}
                      </td>
                      <td className="px-3 py-2 text-zinc-300">{row.mpn || '—'}</td>
                      <td className="px-3 py-2 text-zinc-300">{row.upc || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        <button
                          type="button"
                          onClick={() => handleEditRow(row)}
                          className="mr-2 rounded border border-white/10 px-2 py-1 text-white hover:bg-white/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteRow(row)}
                          className="rounded border border-red-500/40 px-2 py-1 text-red-200 hover:bg-red-500/10"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">No items yet. Add one above or import a CSV.</p>
          )}
        </Section>
      )}
    </div>
  );
}
